/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import type { OrganizationTheme } from "@/lib/themes";
import { setOrgThemeCookie } from "@/lib/utils/design/theme-cookies";
import { useEffect, useLayoutEffect, useRef } from "react";
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from "../constants";
import type { CachedOrgTheme } from "../types";
import { applyOrganizationColors } from "../utils/apply-theme-colors";
import { applyOrganizationTheme } from "../utils/apply-organization-theme";
import { isDarkModeActive } from "../utils/dark-mode";
import {
  writeCachedOrgTheme,
  writeCachedThemeVars,
} from "../utils/theme-storage";

interface UseThemeEffectsOptions {
  primaryColor: string;
  secondaryColor: string;
  organizationTheme: OrganizationTheme | null;
  isMarketing: boolean;
  organizationId?: string;
  cachedTheme: CachedOrgTheme | null;
  settings: {
    primaryColor?: string | null;
    secondaryColor?: string | null;
    themeId?: string | null;
  } | null;
}

/**
 * Hook to manage theme application effects
 */
export function useThemeEffects({
  primaryColor,
  secondaryColor,
  organizationTheme,
  isMarketing,
  organizationId,
  cachedTheme,
  settings,
}: UseThemeEffectsOptions) {
  const lastAppliedRef = useRef<{
    primaryColor: string;
    secondaryColor: string;
    themeId: string | null;
    isDark: boolean;
  } | null>(null);

  const hasAppliedInitialColorsRef = useRef(false);

  // Apply theme synchronously before paint to prevent branding flash.
  // Uses organizationTheme directly (always available since useThemeColors
  // falls back to getDefaultTheme), so we never flash raw hex defaults.
  useLayoutEffect(() => {
    if (hasAppliedInitialColorsRef.current || isMarketing) {
      return;
    }

    const isDark = isDarkModeActive();

    if (organizationTheme) {
      applyOrganizationTheme(organizationTheme, isDark);
    } else if (cachedTheme) {
      const cachedPrimary = cachedTheme.primaryColor ?? DEFAULT_PRIMARY_COLOR;
      const cachedSecondary =
        cachedTheme.secondaryColor ?? DEFAULT_SECONDARY_COLOR;
      applyOrganizationColors(cachedPrimary, cachedSecondary, isDark);
    } else {
      applyOrganizationColors(primaryColor, secondaryColor, isDark);
    }

    if (!isMarketing) {
      setOrgThemeCookie(primaryColor, secondaryColor);
    }

    hasAppliedInitialColorsRef.current = true;
    lastAppliedRef.current = {
      primaryColor,
      secondaryColor,
      themeId: organizationTheme?.id ?? null,
      isDark,
    };
  }, [
    isMarketing,
    cachedTheme,
    organizationTheme,
    primaryColor,
    secondaryColor,
  ]);

  // Unified effect for applying colors - only applies when values actually change
  useEffect(() => {
    const isDark = isDarkModeActive();
    const lastApplied = lastAppliedRef.current;
    const hasCachedTheme = Boolean(
      cachedTheme?.primaryColor ?? cachedTheme?.secondaryColor,
    );
    const usingDefaults =
      primaryColor === DEFAULT_PRIMARY_COLOR &&
      secondaryColor === DEFAULT_SECONDARY_COLOR;
    const currentThemeId = organizationTheme?.id ?? null;

    const hasChanged =
      !lastApplied ||
      lastApplied.primaryColor !== primaryColor ||
      lastApplied.secondaryColor !== secondaryColor ||
      lastApplied.themeId !== currentThemeId ||
      lastApplied.isDark !== isDark;

    if (!hasChanged || (!settings && hasCachedTheme && usingDefaults)) {
      return;
    }

    // Apply full theme if organization has selected a predefined theme
    if (organizationTheme) {
      applyOrganizationTheme(organizationTheme, isDark);
    } else {
      // Fallback to custom primary/secondary colors (legacy support)
      applyOrganizationColors(primaryColor, secondaryColor, isDark);
    }

    if (!isMarketing) {
      setOrgThemeCookie(primaryColor, secondaryColor);
    }

    lastAppliedRef.current = {
      primaryColor,
      secondaryColor,
      themeId: currentThemeId,
      isDark,
    };

    if (!isMarketing) {
      writeCachedOrgTheme({
        orgId: organizationId,
        themeId: organizationTheme?.id,
        primaryColor,
        secondaryColor,
        updatedAt: Date.now(),
      });
      writeCachedThemeVars(isDark);
    }
  }, [
    primaryColor,
    secondaryColor,
    organizationTheme,
    isMarketing,
    organizationId,
    settings,
    cachedTheme,
  ]);

  // Detect dark mode changes separately (only when colors are stable)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = isDarkModeActive();
      const lastApplied = lastAppliedRef.current;

      if (lastApplied && lastApplied.isDark !== isDark) {
        requestAnimationFrame(() => {
          // Re-apply the current theme with new dark mode setting
          if (organizationTheme) {
            applyOrganizationTheme(organizationTheme, isDark);
          } else {
            applyOrganizationColors(
              lastApplied.primaryColor,
              lastApplied.secondaryColor,
              isDark,
            );
          }
          lastAppliedRef.current = {
            ...lastApplied,
            isDark,
          };
        });
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });

    return () => {
      observer.disconnect();
    };
  }, [organizationTheme]); // Re-create observer if theme changes
}
